import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { SharedService } from 'src/app/core/services/shared.service';
import Swal from 'sweetalert2';

// almora 2026.07.28 Componente generico de Horario Semanal (SYS02TIP = 'HOR').
//
// Pinta una rejilla tipo "cuaderno de escuela": una columna por dia (DOMINGO..SABADO, los 7
// siempre visibles) y una fila por franja horaria. Se alimenta del endpoint existente
// ListarGenerico/{SYS02CON} (aktek.ws.MantenimientosCRUD) igual que app-advancedtable: NO
// requiere backend propio ni tabla nueva; cada fila del DataTable es un bloque del horario.
//
// El mapeo fila -> bloque se resuelve por CONVENCION DE ALIAS en el SYS02SQL de la pantalla
// (mismo criterio que ya usa SYS02TIP='CAL' con Subject / CAL01HIN / DIA):
//
//   Nivel BLOQUE (una fila = un bloque)
//     HOR_DIA  (req)  dia. Numerico 1..7 o letra/nombre. Sinonimo: DIA
//     HOR_INI  (req)  hora de inicio 'HH:MM' / 'HH:MM:SS'. Sinonimo: HORA_INI
//     HOR_FIN  (opt)  hora de fin; si falta se asume HOR_INI + intervalo
//     HOR_TIT  (req)  texto principal de la celda. Sinonimo: Subject
//     HOR_SUB  (opt)  segunda linea.               Sinonimo: Description
//     HOR_DET  (opt)  tercera linea / tooltip
//     HOR_COL  (opt)  color de fondo del bloque (#RRGGBB o nombre CSS)
//
//   Nivel REJILLA (opcionales, se leen de la PRIMERA fila; espejo de HORA_INICIO/HORA_FIN de CAL)
//     HORA_INICIO     hora en que arranca la rejilla (default: la mas temprana de los datos)
//     HORA_FIN        hora en que termina la rejilla (default: la mas tardia de los datos)
//     HOR_INT         minutos por franja (default 60)
//     HOR_DIAS        dias visibles, ej '2,3,4,5,6' (default: los 7, domingo a sabado)
//     HOR_DOW         convencion numerica de HOR_DIA:
//                       'DAYOFWEEK' (default) -> 1=Domingo .. 7=Sabado (igual que MySQL DAYOFWEEK)
//                       'ISO'                 -> 1=Lunes   .. 7=Domingo
//
// Interaccion (identica a app-advancedtable para reusar la metadata que ya existe):
//   - Click en un bloque  -> SELECT contra UrlClickLista con las PK de la fila (Estructura A03PKY='S').
//   - Click en celda vacia -> abre el mantenimiento en blanco del boton INSERT del toolbar,
//                             sembrando HOR_DIA / HOR_INI de la celda pulsada.
//   - MsDetalle='S' (caso wizard) -> el mantenimiento se abre embebido con <app-simple-ms>.

interface FranjaHoraria {
  minutos: number;      // minutos desde las 00:00
  label: string;        // 'HH:MM'
}

interface BloqueHorario {
  INDEX: number;
  dia: number;          // 1 = Domingo ... 7 = Sabado (numeracion interna)
  minIni: number;
  minFin: number;
  titulo: string;
  subtitulo: string;
  detalle: string;
  color: string;
  fila: any;            // fila cruda del DataTable (para armar las llaves)
  top: number;          // px
  alto: number;         // px
  izq: number;          // % dentro de la columna del dia
  ancho: number;        // % dentro de la columna del dia
  carril: number;
}

interface ColumnaDia {
  dia: number;
  nombre: string;
  bloques: BloqueHorario[];
}

@Component({
  selector: 'app-horarios',
  templateUrl: './horarios.component.html',
  styleUrls: ['./horarios.component.scss']
})
export class HorariosComponent implements OnInit, OnChanges {

  // Entradas: mismos nombres que app-advancedtable para que el wizard los pase igual.
  @Input() WsURL: string = '';                    // api/mantenimientos/ListarGenerico/{SYS02CON}
  @Input() WsLlaves: any = '';                    // llaves del registro padre (item.Llaves)
  @Input() MsDetalle: string = 'N';               // 'S' => mantenimiento embebido (viene de NAVS)
  @Input() A04CDE = null;                         // titulo heredado del wizard
  @Input() SYS02CON_PAPA_LD: string;
  @Input() strSYS02TIP: string = '';
  @Input() WsFiltrosOpcionales: any = {};
  @Input() ParametrosLocales: { M18COD?: string | number; M19COD?: string } = {};

  @Output() AbrirNuevoNav = new EventEmitter<string>();

  // Numeracion interna: 1 = Domingo ... 7 = Sabado (los 7 dias, siempre en este orden).
  private readonly NOMBRE_DIA = ['', 'Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  private readonly DIAS_DEFECTO = [1, 2, 3, 4, 5, 6, 7];

  // aliases adicionales para aceptar variantes comunes en la metadata
  private readonly ALIAS_HORA_INI = ['HOR_INI', 'HORA_INI', 'HORA_INICIO', 'CAL01HIN', 'CAL01HIN'];
  private readonly ALIAS_HORA_FIN = ['HOR_FIN', 'HORA_FIN', 'CAL01HFI', 'CAL01HFI'];
  private readonly ALIAS_GRID_INICIO = ['HORA_INICIO', 'HORA_INI', 'CAL01HIN', 'CAL01HIN'];
  private readonly ALIAS_GRID_FIN   = ['HORA_FIN', 'HORA_FI', 'CAL01HFI', 'CAL01HFI'];
  private readonly ALIAS_FECHA      = ['FECHA', 'DATE', 'CAL01FEC', 'FEC', 'FECHA_INI', 'FECHA_INICIO', 'START_DATE'];

  readonly ALTO_SLOT = 46;                        // px por franja horaria

  TituloPantalla = '';
  toolbar: any[] = [];
  operaciones: any[] = [];
  estructura: any[] = [];
  llaves: string[] = [];                          // campos PK (A03PKY = 'S')
  permitir_click_lista = 'N';
  UrlClickLista = '';

  slots: FranjaHoraria[] = [];
  columnas: ColumnaDia[] = [];
  gridCols = '';                                  // grid-template-columns calculado
  intervalo = 60;                                 // minutos por franja
  minGrid = 0;                                    // minuto inicial de la rejilla
  maxGrid = 0;                                    // minuto final de la rejilla
  dowISO = false;                                 // HOR_DOW = 'ISO'

  cargando = true;
  sinDatos = false;
  WsMsDataInterno: any = '';

  private dataTableCompleta: any[] = [];
  
  private dataTableFiltrada: any[] = [];

  private elementosOcultados: { el: HTMLElement; display: string }[] = [];

  constructor(private dataService: SharedService) { }

  ngOnInit(): void {
    this.cargar();
    // Para pruebas locales sin backend: descomenta la siguiente línea y comenta this.cargar() anterior
    // this.debugLocal();
  }

  ngOnChanges(changes: SimpleChanges): void {
  const necesitaRecargar = (
    (changes.WsURL && !changes.WsURL.firstChange) ||
    (changes.WsLlaves && !changes.WsLlaves.firstChange) ||
    (changes.WsFiltrosOpcionales && !changes.WsFiltrosOpcionales.firstChange)
  );

  if (necesitaRecargar) {
    this.cargar();
  }

  if (changes.ParametrosLocales && !changes.ParametrosLocales.firstChange) {
    this.aplicarFiltrosLocales();
  }
}

  // ------------------------------------------------------------------
  // Carga (ListarGenerico)
  // ------------------------------------------------------------------

  // PAGINA = -1 => el backend no aplica LIMIT y devuelve el horario completo (igual que 'CAL').
  cargar(): void {
    this.cargando = true;

    if (this.WsLlaves == null || this.WsLlaves === '') this.WsLlaves = [];

    const body: any = {
      FILTROS_OPCIONALES: [
        this.WsFiltrosOpcionales || {}
      ],
      FILTROS_OBLIGATORIOS: this.WsLlaves,
      PAGINA: -1,
      OFFSET: 0
    };
    if (this.SYS02CON_PAPA_LD != null) body.SYS02CON_PAPA_LD = this.SYS02CON_PAPA_LD;

    this.dataService.ConsultarWS(this.WsURL, body).subscribe(
      (datosResp: any) => {
        console.log('Horario - payload recibido:', datosResp); // <-- debug temporal
        this.TituloPantalla       = datosResp.SYS02DES || '';
        this.estructura           = datosResp.Estructura || [];
        this.operaciones          = datosResp.Operaciones || [];
        this.permitir_click_lista = datosResp.PermitirClickLista || 'N';
        this.UrlClickLista        = datosResp.UrlClickLista || '';
        this.toolbar              = this.leerToolbar(datosResp.Toolbar);
        this.llaves               = this.estructura.filter((c: any) => c.A03PKY === 'S').map((c: any) => c.A03COD);

        this.dataTableCompleta = datosResp.DataTable || [];
        this.aplicarFiltrosLocales();
        this.cargando = false;
        this.cargando = false;
      },
      (err: any) => {
        this.cargando = false;
        this.armarHorario([]);
        Swal.fire('Error', err || 'No fue posible cargar el horario', 'error');
      });
  }

  // Toolbar viene como [{ NORMALES:[...], OPERACIONES:[...] }] (formato de ListarGenerico).
  private leerToolbar(toolbar: any): any[] {
    if (!toolbar || !toolbar.length) return [];
    return toolbar[0].NORMALES || [];
  }

  recargar(): void {
    this.cargar();
  }

  // ------------------------------------------------------------------
  // Armado de la rejilla
  // ------------------------------------------------------------------

  private armarHorario(data: any[]): void {
    const config = data.length ? data[0] : {};
    this.dowISO    = String(this.valor(config, ['HOR_DOW']) || '').toUpperCase() === 'ISO';
    this.intervalo = this.aEntero(this.valor(config, ['HOR_INT']), 60);
    if (this.intervalo <= 0) this.intervalo = 60;

    // 1) Filas -> bloques
    const bloques: BloqueHorario[] = [];
    data.forEach((fila: any, i: number) => {
      // 1a) intentar leer dia directo (HOR_DIA/DIA)
      const diaDirecto = this.parsearDia(this.valor(fila, ['HOR_DIA', 'DIA']));
      // 1b) posibles horas
      const iniVal = this.valor(fila, this.ALIAS_HORA_INI);
      const finVal = this.valor(fila, this.ALIAS_HORA_FIN);

      // 1c) si hay fechas en la fila (campo FECHA o variantes), recolectarlas (pueden ser varias)
      const fechaVal = this.valor(fila, this.ALIAS_FECHA);

      // armado de lista de dias (internos 1..7)
      const diasList: number[] = [];
      if (diaDirecto && diaDirecto !== 0) {
        diasList.push(diaDirecto);
      } else if (fechaVal) {
        // puede venir como "2026-07-27,2026-07-29" o "27/07/2026" o "2026-07-27 - 2026-07-29"
        const raw = String(fechaVal);
        // separar por comas o punto y comas
        const partes = raw.split(/[;,]/).map(s => s.trim()).filter(s => s !== '');
        for (const p of partes) {
          // si es un rango con guion, tomamos la primera fecha (puedes cambiar la lógica si prefieres expandir rangos)
          let candidato = p;
          if (p.indexOf('-') > 0 && /\d{4}-\d{2}-\d{2}/.test(p)) {
            // "2026-07-10 - 2026-07-12" -> coger la izquierda
            candidato = p.split('-').slice(0, 3).join('-').trim();
          }
          const dnum = this.fechaADia(candidato);
          if (dnum >= 1 && dnum <= 7) diasList.push(dnum);
        }
      }

      // Si no hay dia ni fecha válida, intentar seguir (se ignora la fila)
      if (!diasList.length) return;

      // parsear hora de inicio y fin (si vienen como datetime, parsearHora se encarga)
      const ini = this.parsearHora(iniVal);
      if (ini === null) return; // no se puede ubicar sin hora de inicio
      let fin = this.parsearHora(finVal);
      if (fin === null || fin <= ini) fin = ini + this.intervalo;

      // por cada dia detectado, crear un bloque (si la fila trae varias fechas -> varios bloques)
      diasList.forEach((diaEntry, idx) => {
        bloques.push({
          INDEX: (i * 10) + idx,
          dia: diaEntry,
          minIni: ini,
          minFin: fin,
          titulo:    this.texto(this.valor(fila, ['HOR_TIT', 'Subject'])),
          subtitulo: this.texto(this.valor(fila, ['HOR_SUB', 'Description', 'M35DES'])),
          detalle:   this.texto(this.valor(fila, ['HOR_DET'])),
          color:     this.texto(this.valor(fila, ['HOR_COL'])),
          fila: fila,
          top: 0, alto: 0, izq: 0, ancho: 100, carril: 0
        });
      });

    });

    this.sinDatos = bloques.length === 0;

    // 2) Rango horario de la rejilla
    let ini = this.parsearHora(this.valor(config, this.ALIAS_GRID_INICIO));
    let fin = this.parsearHora(this.valor(config, this.ALIAS_GRID_FIN));
    if (ini === null) {
      ini = bloques.length ? Math.min(...bloques.map(b => b.minIni)) : 7 * 60;
      ini = Math.floor(ini / this.intervalo) * this.intervalo;
    }
    if (fin === null) {
      fin = bloques.length ? Math.max(...bloques.map(b => b.minFin)) : 19 * 60;
      fin = Math.ceil(fin / this.intervalo) * this.intervalo;
    }
    // Si hay bloques fuera del rango configurado, se amplia para no ocultarlos.
    bloques.forEach(b => {
      if (b.minIni < ini) ini = Math.floor(b.minIni / this.intervalo) * this.intervalo;
      if (b.minFin > fin) fin = Math.ceil(b.minFin / this.intervalo) * this.intervalo;
    });
    if (fin <= ini) fin = ini + this.intervalo;

    this.minGrid = ini;
    this.maxGrid = fin;

    this.slots = [];
    for (let m = ini; m < fin; m += this.intervalo) {
      this.slots.push({ minutos: m, label: this.aHHMM(m) });
    }

    // 3) Columnas (dias). Por defecto los 7, de Domingo a Sabado.
    const dias = this.parsearDias(this.valor(config, ['HOR_DIAS']));
    this.columnas = dias.map(d => ({
      dia: d,
      nombre: this.NOMBRE_DIA[d],
      bloques: bloques.filter(b => b.dia === d)
    }));
    this.gridCols = '90px repeat(' + this.columnas.length + ', minmax(120px, 1fr))';

    // 4) Posicion de cada bloque dentro de su columna
    this.columnas.forEach(col => this.acomodarColumna(col.bloques));
  }

  // Calcula top/alto (px) y carriles para bloques solapados dentro de un mismo dia.
  private acomodarColumna(bloques: BloqueHorario[]): void {
    bloques.forEach(b => {
      b.top  = ((b.minIni - this.minGrid) / this.intervalo) * this.ALTO_SLOT;
      b.alto = ((b.minFin - b.minIni) / this.intervalo) * this.ALTO_SLOT;
      if (b.alto < 22) b.alto = 22;
      b.izq = 0;
      b.ancho = 100;
      b.carril = 0;
    });

    const orden = bloques.slice().sort((a, b) => (a.minIni - b.minIni) || (a.minFin - b.minFin));
    let grupo: BloqueHorario[] = [];
    let finGrupo = -1;

    const cerrarGrupo = () => {
      if (!grupo.length) return;
      const finCarril: number[] = [];               // minuto final ocupado por cada carril
      grupo.forEach(b => {
        let c = finCarril.findIndex(f => f <= b.minIni);
        if (c === -1) { c = finCarril.length; finCarril.push(b.minFin); }
        else finCarril[c] = b.minFin;
        b.carril = c;
      });
      const total = finCarril.length;
      grupo.forEach(b => {
        b.ancho = 100 / total;
        b.izq   = (b.carril * 100) / total;
      });
      grupo = [];
    };

    orden.forEach(b => {
      if (grupo.length && b.minIni >= finGrupo) { cerrarGrupo(); finGrupo = -1; }
      grupo.push(b);
      if (b.minFin > finGrupo) finGrupo = b.minFin;
    });
    cerrarGrupo();
  }

  // ------------------------------------------------------------------
  // Parseo de la convencion de alias
  // ------------------------------------------------------------------

  // Primer alias presente y no vacio en la fila.
  private valor(fila: any, alias: string[]): any {
    if (!fila) return null;
    for (const a of alias) {
      const v = fila[a];
      if (v !== undefined && v !== null && String(v).trim() !== '') return v;
    }
    return null;
  }

  private texto(v: any): string {
    return v == null ? '' : String(v);
  }

  private aEntero(v: any, porDefecto: number): number {
    const n = parseInt(String(v), 10);
    return isNaN(n) ? porDefecto : n;
  }

  /** Devuelve 1=Domingo .. 7=Sabado, o 0 si no se puede interpretar. */
  private parsearDia(v: any): number {
    if (v == null) return 0;
    const raw = String(v).trim();
    if (raw === '') return 0;

    // Numerico. Se tolera el formato con decimales que devuelve un campo SYS03TIP='N' ("1.00").
    const num = raw.replace(/,/g, '');
    if (/^\d+(\.\d+)?$/.test(num)) {
      const n = Math.round(parseFloat(num));
      if (this.dowISO) {                            // 1=Lunes..7=Domingo -> interno
        if (n < 1 || n > 7) return 0;
        return n === 7 ? 1 : n + 1;
      }
      return (n >= 1 && n <= 7) ? n : 0;            // fuera de 1..7 => fila ignorada
    }

    // Texto: nombre completo, abreviatura o letra (notacion escolar L K M J V S D)
    const t = raw.toUpperCase()
                 .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (t.startsWith('DOM') || t.startsWith('SUN')) return 1;
    if (t.startsWith('LUN') || t.startsWith('MON')) return 2;
    if (t.startsWith('MAR') || t.startsWith('TUE')) return 3;
    if (t.startsWith('MIE') || t.startsWith('WED')) return 4;
    if (t.startsWith('JUE') || t.startsWith('THU')) return 5;
    if (t.startsWith('VIE') || t.startsWith('FRI')) return 6;
    if (t.startsWith('SAB') || t.startsWith('SAT')) return 7;
    switch (t) {
      case 'D': return 1;
      case 'L': return 2;
      case 'K': return 3;
      case 'M': return 4;
      case 'J': return 5;
      case 'V': return 6;
      case 'S': return 7;
    }
    return 0;
  }

  /** Convierte un valor de fecha a dia interno 1=Domingo..7=Sabado. */
  private fechaADia(v: any): number {
    if (v == null) return 0;
    let s = String(v).trim();
    if (s === '') return 0;

    // soportar formatos: 'YYYY-MM-DD', 'YYYY-MM-DD HH:MM:SS', 'DD/MM/YYYY', timestamp numerico, etc.
    // Si viene como rango "YYYY-MM-DD - YYYY-MM-DD" -> tomamos la primera parte (ya recortada en el caller)
    // Detectar DD/MM/YYYY
    let dateObj: Date | null = null;
    const ddmmyyyy = /^\d{1,2}\/\d{1,2}\/\d{2,4}$/;
    if (ddmmyyyy.test(s)) {
      const parts = s.split('/');
      const d = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10) - 1;
      let y = parseInt(parts[2], 10);
      if (y < 100) y += 2000;
      dateObj = new Date(y, m, d);
    } else if (/^\d+$/.test(s)) {
      // posible timestamp en ms o en segundos o 'YYYYMMDD'
      if (s.length >= 12) { // probablemente ms
        const n = parseInt(s, 10);
        dateObj = new Date(n);
      } else if (s.length === 8) { // YYYYMMDD
        const y = parseInt(s.substr(0,4),10);
        const m = parseInt(s.substr(4,2),10)-1;
        const d = parseInt(s.substr(6,2),10);
        dateObj = new Date(y,m,d);
      } else {
        // segundos -> ms
        const n = parseInt(s,10) * 1000;
        dateObj = new Date(n);
      }
    } else {
      // Try native parse for ISO-ish strings (YYYY-MM-DD or with time/T)
      const sepIndex = s.indexOf(' ');
      if (sepIndex > 0) s = s.split(' ')[0];
      const sISO = s.includes('T') ? s : s; // leave as is
      const parsed = Date.parse(sISO);
      if (!isNaN(parsed)) dateObj = new Date(parsed);
    }

    if (!dateObj || isNaN(dateObj.getTime())) return 0;
    const jsDay = dateObj.getDay(); // 0=Domingo .. 6=Sabado
    // mapear a interno 1..7
    return jsDay === 0 ? 1 : jsDay + 1;
  }

  /** 'HH:MM', 'HH:MM:SS', '2026-01-01 09:30:00' o entero de hora -> minutos desde 00:00. */
  private parsearHora(v: any): number {
    if (v == null) return null;
    let s = String(v).trim();
    if (s === '') return null;
    if (s.indexOf(' ') > 0) s = s.split(' ')[1] || '';     // datetime -> parte de hora
    if (s.indexOf('T') > 0) s = s.split('T')[1] || '';

    // Solo ':' como separador: '9.5' seria ambiguo (9:05 vs 09:30) y el contrato pide HH:MM.
    const m = s.match(/^(\d{1,2}):(\d{1,2})/);
    if (m) {
      const h  = parseInt(m[1], 10);
      const mi = parseInt(m[2], 10);
      if (isNaN(h) || isNaN(mi)) return null;
      return (h * 60) + mi;
    }
    if (/^\d{1,2}$/.test(s)) return parseInt(s, 10) * 60;   // '9' => 09:00
    if (/^\d{4}$/.test(s)) return (parseInt(s.substr(0, 2), 10) * 60) + parseInt(s.substr(2, 2), 10);
    return null;
  }

  private aHHMM(min: number): string {
    const h = Math.floor(min / 60) % 24;
    const m = min % 60;
    return (h < 10 ? '0' : '') + h + ':' + (m < 10 ? '0' : '') + m;
  }

  /** HOR_DIAS ('1,2,3') -> dias visibles. Por defecto los 7, de Domingo a Sabado. */
  private parsearDias(v: any): number[] {
    if (v == null) return this.DIAS_DEFECTO.slice();
    const dias = String(v).split(',')
      .map(x => this.parsearDia(x.trim()))
      .filter(d => d >= 1 && d <= 7);
    // sin duplicados y conservando el orden indicado
    const unicos = dias.filter((d, i) => dias.indexOf(d) === i);
    return unicos.length ? unicos : this.DIAS_DEFECTO.slice();
  }

  // ------------------------------------------------------------------
  // Estilos de bloque (se calculan en TS para no meter logica en el template)
  // ------------------------------------------------------------------

  estiloBloque(b: BloqueHorario): any {
    const estilo: any = {
      'top.px': b.top,
      'height.px': b.alto,
      'left': b.izq + '%',
      'width': 'calc(' + b.ancho + '% - 4px)'
    };
    if (b.color !== '') estilo['background-color'] = b.color;
    return estilo;
  }

  rangoBloque(b: BloqueHorario): string {
    return this.aHHMM(b.minIni) + ' - ' + this.aHHMM(b.minFin);
  }

  tooltipBloque(b: BloqueHorario): string {
    return [this.rangoBloque(b), b.titulo, b.subtitulo, b.detalle]
      .filter(x => x !== '' && x != null).join(' · ');
  }

  // ------------------------------------------------------------------
  // Navegacion (mismo contrato que app-advancedtable)
  // ------------------------------------------------------------------

  private toJSON(operacion: string, arr: any[], url?: string, extra?: string) {
    const NavID = (Date.now().toString(36) + Math.random().toString(36).substr(2, 5)).toUpperCase();
    return JSON.parse('{ "LLAVES"   :' + JSON.stringify(arr) +
      (url != null ? (', "URL"      :"' + url + '"') : '') +
      (extra != null ? (',' + extra) : '') +
      ', "NAV_ID"   : "' + NavID + '"' +
      ', "OPERACION": "' + operacion + '"}');
  }

  private extraLlaves(): string {
    return '  "FILTROS_OPCIONALES"   : [{}]' +
      ', "FILTROS_OBLIGATORIOS" : ' + JSON.stringify(this.WsLlaves) +
      (this.SYS02CON_PAPA_LD != null ? (', "SYS02CON_PAPA_LD" : ' + JSON.stringify(this.SYS02CON_PAPA_LD)) : '');
  }

  private AbrirURL(json: any): void {
    if (this.MsDetalle === 'S') this.WsMsDataInterno = json;   // mantenimiento embebido (wizard)
    else this.AbrirNuevoNav.emit(json);                        // nueva pestaña
  }

  EventoCerrarMSInterno(objRefrescar: any): void {
    this.WsMsDataInterno = '';
    if (objRefrescar) this.cargar();
  }

  /** Llaves (PK) de la fila del bloque, mezcladas con las del padre (igual que advancedtable). */
  private llavesDe(b: BloqueHorario): any {
    const llaves: any = {};
    this.llaves.forEach(k => llaves[k] = b.fila[k]);
    if (this.WsLlaves && this.WsLlaves.length) {
      for (const prop in this.WsLlaves[0]) {
        if (llaves[prop] == null) llaves[prop] = this.WsLlaves[0][prop];
        else llaves[prop + '_1'] = this.WsLlaves[0][prop];
      }
    }
    return llaves;
  }

  /** Click en un bloque -> abre el mantenimiento del registro. */
  clickBloque(b: BloqueHorario): void {
    if (this.permitir_click_lista !== 'S' || this.UrlClickLista === '') return;
    this.AbrirURL(this.toJSON('SELECT', [this.llavesDe(b)], this.UrlClickLista));
  }

  private botonInsert(): any {
    return this.toolbar.find(x => x.Evento === 'INSERT' || x.Evento === 'INSERT_GENERICO') || null;
  }

  get hayInsert(): boolean {
    return this.botonInsert() != null;
  }

  /** Click en una celda vacia -> mantenimiento en blanco con el dia y la hora sembrados. */
  clickVacio(col: ColumnaDia, slot: FranjaHoraria): void {
    const boton = this.botonInsert();
    if (!boton) return;
    const semilla: any = { HOR_DIA: this.diaExterno(col.dia), HOR_INI: slot.label };
    this.AbrirURL(this.toJSON(boton.Evento, [semilla], boton.URL, this.extraLlaves()));
  }

  /** Convierte el dia interno (1=Domingo) al formato que espera la metadata del cliente. */
  private diaExterno(dia: number): number {
    if (!this.dowISO) return dia;
    return dia === 1 ? 7 : dia - 1;
  }

  /** Botones del toolbar: INSERT abre mantenimiento en blanco; el resto hace POST y recarga. */
  clickToolbar(boton: any): void {
    if (boton.Evento === 'INSERT' || boton.Evento === 'INSERT_GENERICO') {
      this.AbrirURL(this.toJSON(boton.Evento, [{}], boton.URL, this.extraLlaves()));
      return;
    }
    this.dataService.ConsultarWS(boton.URL, this.toJSON(boton.Evento, [], null, this.extraLlaves())).subscribe(
      () => {
        Swal.fire({
          toast: true, position: 'bottom-end', icon: 'success',
          text: (boton.boton || 'Proceso') + ' ejecutado correctamente',
          showConfirmButton: false, timer: 3000
        });
        this.cargar();
      },
      (err: any) => Swal.fire({
        title: 'Error', icon: 'error',
        html: '<div style="white-space:pre-line">' + (err || '') + '</div>'
      }));
  }

  private aplicarFiltrosLocales(): void {
    let data = Array.isArray(this.dataTableCompleta) ? this.dataTableCompleta.slice() : [];
  
    const m18 = this.ParametrosLocales?.M18COD != null ? String(this.ParametrosLocales.M18COD).trim() : '';
    const m19 = this.ParametrosLocales?.M19COD != null ? String(this.ParametrosLocales.M19COD).trim() : '';
  
    if (m18) {
      data = data.filter(item => {
        const val = (item.M18COD != null) ? String(item.M18COD).trim() : '';
        return val === m18;
      });
    }
  
    if (m19) {
      data = data.filter(item => {
        const val = (item.M19COD != null) ? String(item.M19COD).trim() : '';
        return val === m19;
      });
    }
  
    this.dataTableFiltrada = data;
    this.armarHorario(this.dataTableFiltrada);
  }

  imprimirHorario(): void {
    this.generarAreaImpresion();
    this.ocultarParaImprimir();
  
    const limpiar = () => {
      this.restaurarDespuesImprimir();
      window.removeEventListener('afterprint', limpiar);
    };
    window.addEventListener('afterprint', limpiar);
  
    setTimeout(() => window.print(), 50);
  }
  
  private ocultarParaImprimir(): void {
    this.elementosOcultados = [];
    Array.from(document.body.children).forEach((el: Element) => {
      const htmlEl = el as HTMLElement;
      if (htmlEl.id !== 'hs-print-area') {
        this.elementosOcultados.push({ el: htmlEl, display: htmlEl.style.display });
        htmlEl.style.setProperty('display', 'none', 'important');
      }
    });
  }
  
  private restaurarDespuesImprimir(): void {
    this.elementosOcultados.forEach(({ el, display }) => {
      if (display) el.style.display = display;
      else el.style.removeProperty('display');
    });
    this.elementosOcultados = [];
  
    const area = document.getElementById('hs-print-area');
    if (area) area.remove();
  
    const estiloPagina = document.getElementById('hs-print-style');
    if (estiloPagina) estiloPagina.remove();
  }
  
  private generarAreaImpresion(): void {
    const anterior = document.getElementById('hs-print-area');
    if (anterior) anterior.remove();
  
    const anteriorEstilo = document.getElementById('hs-print-style');
    if (anteriorEstilo) anteriorEstilo.remove();
  
    const estiloPagina = document.createElement('style');
    estiloPagina.id = 'hs-print-style';
    estiloPagina.textContent = '@page { size: landscape; margin: 12mm; }';
    document.head.appendChild(estiloPagina);
  
    const area = document.createElement('div');
    area.id = 'hs-print-area';
    area.style.cssText = 'display:block; font-family:Arial,Helvetica,sans-serif; padding:0; margin:0; background:#fff;';
  
    const fecha = new Date().toLocaleDateString('es-PA', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  
    const colorAdjust = '-webkit-print-color-adjust:exact; print-color-adjust:exact; color-adjust:exact;';
  
    type CeldaCalculada = { tipo: 'start'; bloque: BloqueHorario; rowSpan: number } | { tipo: 'skip' } | { tipo: 'empty' };
  
    const filasPorColumna: CeldaCalculada[][] = this.columnas.map(col => {
      const filas: CeldaCalculada[] = this.slots.map(() => ({ tipo: 'empty' }));
      const bloquesOrdenados = col.bloques.slice().sort((a, b) => a.minIni - b.minIni);
  
      bloquesOrdenados.forEach(b => {
        let startIdx = 0;
        for (let i = 0; i < this.slots.length; i++) {
          if (this.slots[i].minutos <= b.minIni) startIdx = i;
          else break;
        }
        let endIdx = startIdx;
        for (let i = 0; i < this.slots.length; i++) {
          if (this.slots[i].minutos < b.minFin) endIdx = i;
        }
        if (endIdx < startIdx) endIdx = startIdx;
  
        if (filas[startIdx].tipo !== 'empty') return;
  
        filas[startIdx] = { tipo: 'start', bloque: b, rowSpan: endIdx - startIdx + 1 };
        for (let i = startIdx + 1; i <= endIdx && i < filas.length; i++) {
          filas[i] = { tipo: 'skip' };
        }
      });
  
      return filas;
    });
  
    let filasHtml = '';
    this.slots.forEach((slot, filaIdx) => {
      filasHtml += `<tr>
        <td style="border:1px solid #cbd5e1; padding:4px 6px; font-size:10px; color:#64748b; text-align:center; vertical-align:top; width:60px;">
          ${slot.label}
        </td>`;
  
      this.columnas.forEach((col, colIdx) => {
        const celda = filasPorColumna[colIdx][filaIdx];
  
        if (celda.tipo === 'skip') {
          return;
        }
  
        if (celda.tipo === 'empty') {
          filasHtml += `<td style="border:1px solid #cbd5e1; padding:4px 6px; height:26px;"></td>`;
          return;
        }
  
        const bloque = celda.bloque;
        filasHtml += `
          <td rowspan="${celda.rowSpan}" style="border:1px solid #cbd5e1; padding:4px 6px; vertical-align:top; background:#dbeeff; ${colorAdjust}">
            <div style="font-size:9px; color:#475569;">${this.rangoBloque(bloque)}</div>
            <div style="font-size:10.5px; font-weight:700; color:#0f2d3d; line-height:1.2;">${bloque.titulo}</div>
            ${bloque.subtitulo ? `<div style="font-size:9px; color:#64748b;">${bloque.subtitulo}</div>` : ''}
          </td>`;
      });
  
      filasHtml += `</tr>`;
    });
  
    const encabezadoCols = this.columnas.map(c => `
      <th style="border:1px solid #145a80; background:#1a6f9c; color:#fff; font-size:11px; text-transform:uppercase; padding:8px 6px; ${colorAdjust}">
        ${c.nombre}
      </th>`).join('');
  
    area.innerHTML = `
      <div style="display:flex; justify-content:space-between; align-items:flex-end; border-bottom:3px solid #1a9fd4; padding-bottom:10px; margin-bottom:16px;">
        <div>
          <div style="font-size:22px; font-weight:700; color:#0f2d3d;">Horario Semanal</div>
        </div>
        <div style="font-size:11px; color:#94a3b8;">Generado: ${fecha}</div>
      </div>
      <table style="width:100%; border-collapse:collapse;">
        <thead>
          <tr>
            <th style="border:1px solid #145a80; background:#1a6f9c; color:#fff; font-size:11px; text-transform:uppercase; padding:8px 6px; width:60px; ${colorAdjust}">Hora</th>
            ${encabezadoCols}
          </tr>
        </thead>
        <tbody>${filasHtml}</tbody>
      </table>
      <div style="margin-top:14px; font-size:9px; color:#94a3b8; text-align:right;">AVATAR.SYS</div>
    `;
  
    document.body.appendChild(area);
  }

}